"use client";
import { AuthBackButton } from "@/components/auth/common/auth-back-button";
import { DialogStepper } from "@/components/common/dialog-stepper";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { H2 } from "@/components/ui/heading";
import { Icon } from "@/components/ui/icon";
import type { DetailedListing } from "@/lib/types";
import { useInquirySender } from "@/hooks/use-inquiry-sender";
import Link from "next/link";
import Confetti from "react-confetti";
import { useListingInquiryDialog } from "./inquiry/use-listing-inquiry-dialog";
import { InquiryFormPanel } from "./inquiry/inquiry-form-panel";
import { InquiryAuthPanels } from "./inquiry/inquiry-auth-panels";

export type ListingInquiryDialogProps = {
  listing: DetailedListing;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSent?: () => void;
};

export function ListingInquiryDialog(props: ListingInquiryDialogProps) {
  const { user } = useInquirySender();
  return (
    <InquiryDialogContent
      key={user?.delegated ? user.id : "personal"}
      {...props}
    />
  );
}

function InquiryDialogContent(props: ListingInquiryDialogProps) {
  const model = useListingInquiryDialog(props);
  const { open } = props;
  const {
    t,
    step,
    isDesktop,
    isNonClosable,
    handleOpenChange,
    showBackButton,
    handleBack,
    getTitle,
    showStepper,
    stepperSteps,
    stepperCurrentStep,
    conversationUrl,
    isConfettiComplete,
    width,
    height,
  } = model;
  const titleElement = (
    <H2 className="flex min-w-0 items-center gap-2 text-left">
      {showBackButton() && <AuthBackButton onClick={handleBack} />}
      <span className="block min-w-0 truncate sm:whitespace-normal">
        {getTitle()}
      </span>
    </H2>
  );

  const stepsContent = (
    <>
      {step === "form" && <InquiryFormPanel model={model} />}
      <InquiryAuthPanels model={model} />

      {step === "submitting" && (
        <div className="flex flex-col items-center gap-4 py-8">
          <div className="size-8 border-2 border-foreground/20 border-t-foreground rounded-full animate-spin" />
          <p className="text-sm text-muted-foreground">
            Sending your inquiry...
          </p>
        </div>
      )}

      {step === "success" && (
        <div className="relative flex flex-col items-center gap-4 py-4 overflow-hidden">
          <div className="size-12 rounded-full bg-theme-green text-white flex items-center justify-center">
            <Icon icon="CheckSolid" />
          </div>

          <p className="text-center text-sm text-pretty">
            {t("saas.listing.sidebar.sent.description")}
          </p>

          {conversationUrl && (
            <Button className="w-full" asChild>
              <Link href={conversationUrl}>
                {t("saas.listing.sidebar.sent.footer.open")}
              </Link>
            </Button>
          )}

          <Button
            variant="outline"
            className="w-full"
            onClick={() => handleOpenChange(false)}
          >
            Close
          </Button>
        </div>
      )}
    </>
  );

  if (isDesktop) {
    return (
      <>
        <Dialog open={open} onOpenChange={handleOpenChange}>
          <DialogContent
            closable={!isNonClosable}
            displayCloseButton={!isNonClosable}
            className="sm:max-w-md"
          >
            <DialogHeader>
              <DialogTitle asChild>{titleElement}</DialogTitle>
            </DialogHeader>
            {showStepper && (
              <DialogStepper
                steps={stepperSteps}
                currentStep={stepperCurrentStep}
              />
            )}
            {stepsContent}
          </DialogContent>
        </Dialog>
        {step === "success" && (
          <Confetti
            recycle={!isConfettiComplete}
            width={width}
            height={height}
            style={{ position: "fixed", top: 0, left: 0, zIndex: 9999 }}
          />
        )}
      </>
    );
  }

  return (
    <>
      <Drawer
        open={open}
        onOpenChange={handleOpenChange}
        dismissible={!isNonClosable}
      >
        <DrawerContent>
          <div className="overflow-y-auto px-4 pb-[calc(env(safe-area-inset-bottom)+1.5rem)]">
            <DrawerHeader className="px-0">
              <DrawerTitle asChild>{titleElement}</DrawerTitle>
            </DrawerHeader>
            {showStepper && (
              <DialogStepper
                steps={stepperSteps}
                currentStep={stepperCurrentStep}
              />
            )}
            {stepsContent}
          </div>
        </DrawerContent>
      </Drawer>
      {step === "success" && (
        <Confetti
          recycle={!isConfettiComplete}
          width={width}
          height={height}
          style={{ position: "fixed", top: 0, left: 0, zIndex: 9999 }}
        />
      )}
    </>
  );
}

ListingInquiryDialog.displayName = "ListingInquiryDialog";
